"use client";

import { FC, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Conditional from "@repo/shared-ui/Conditional";
import { BottomNavigationProps } from "../../interfaces/IHeader.interface";
import {
  getChildByNodeName,
  openRedirectURL,
} from "@repo/shared-utils/common-utils";
import BFLLink from "@repo/shared-ui/BFLLink";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { HAMBURGER_PROMPT_URL } from "../../constants/HeaderConstants";
import {
  handleBottomNavigationMenuClickDL,
  handleBottomNavigationIconClickDL,
} from "./MobileNavigationAnalyticsFunction";

const HamburgerMenu: any = dynamic(() => import("./HamburgerMenu.tsx") as any, {
  ssr: false,
});
const QuickActions: any = dynamic(() => import("./QuickActions.tsx") as any, {
  ssr: false,
});

const BottomNavigation: FC<BottomNavigationProps> = ({
  bottomnavigationmobile,
  bottomNavigationMenu,
}) => {
  // State to manage which panel is active: 'none', 'hamburger', or 'quickActions'
  const [activePanel, setActivePanel] = useState<
    "none" | "hamburger" | "quickActions"
  >("none");

  // Helper function to get CSS classes for navigation items
  const getNavigationItemClasses = (
    isQuickActions: boolean,
    isHamburger: boolean,
  ): string => {
    if (isQuickActions && activePanel === "quickActions") {
      return "bg-lgray-200 rounded-[0_0_45%_45%] text-orange-500";
    }
    if (isHamburger && activePanel === "hamburger") {
      return "text-orange-500 content-[''] before:absolute before:left-0 before:top-0 before:h-[3px] before:w-full before:bg-orange-500 before:content-['']";
    }
    return "text-bfl-blue";
  };

  useEffect(() => {
    if (activePanel !== "none") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [activePanel]);

  // Helper function to safely extract menu items
  const extractMenuItems = (node: any): any[] => {
    if (!node) return [];
    if (Array.isArray(node)) return node;
    if (node.children && Array.isArray(node.children)) return node.children;
    return [];
  };

  // Find the hamburger menu from bottomnavigationmobile structure
  const hamburgerMenuItem = bottomnavigationmobile?.[0]?.children?.find(
    (item: any) => item.nodeName === "hamburgermenu",
  );

  // Get the menu lists using getChildByNodeName
  const hamburgerMenuListNode = getChildByNodeName(
    hamburgerMenuItem,
    "hamburgerMenuList",
  );
  const mainMenuListNode = getChildByNodeName(
    hamburgerMenuItem,
    "mainMenuList",
  );
  const chipsDetailsNode = getChildByNodeName(
    hamburgerMenuItem,
    "chipsDetails",
  );

  const mobileFollowus = getChildByNodeName(
    hamburgerMenuItem,
    "followUsMenuList",
  );
  // Extract the actual menu items
  const chipsDetails = extractMenuItems(chipsDetailsNode);
  const hamburgerMenuList = extractMenuItems(hamburgerMenuListNode);
  const mainMenuList = extractMenuItems(mainMenuListNode);

  const quickActions =
    bottomNavigationMenu?.children?.find((item) => item.nodeName === "item2") ||
    {};

  return (
    <>
      <ul className="flex-between-center fixed bottom-0 left-0 z-[9] mb-0 w-full items-end gap-3 bg-white px-4 shadow-[0_0_10px_#0003]">
        {bottomNavigationMenu?.children &&
          bottomNavigationMenu.children.length > 0 &&
          bottomNavigationMenu.children.map((item, index: number) => {
            const isQuickActions = item.nodeName === "item2";
            const isHamburger = item.nodeName === "item4";
            const navigationClasses = getNavigationItemClasses(
              isQuickActions,
              isHamburger,
            );

            return (
              <li
                key={item.nodeName || index}
                className="fs-10 max-h-[63px] flex-1 text-center"
              >
                <BFLLink
                  href={
                    !isQuickActions && !isHamburger ? item.ctaLink : undefined
                  }
                  onClick={() => {
                    // Track analytics for bottom navigation icon click
                    if (isHamburger) {
                      handleBottomNavigationMenuClickDL(item.label);
                      if (isOpenInApp()) {
                        openRedirectURL(HAMBURGER_PROMPT_URL);
                      } else {
                        setActivePanel("hamburger");
                      }
                    } else if (isQuickActions) {
                      handleBottomNavigationIconClickDL(item.label, index);
                      setActivePanel("quickActions");
                    } else {
                      handleBottomNavigationIconClickDL(
                        item.label,
                        index,
                        item.ctaLink,
                      );
                    }
                  }}
                  className={`relative flex w-full flex-col items-center py-3 ${navigationClasses}`}
                >
                  {index === 0 && (
                    <i className="icomoon text-2xl before:content-['\e957']" />
                  )}
                  {index === 1 && (
                    <i className="icomoon text-2xl before:content-['\e954']" />
                  )}
                  {index === 2 && (
                    <i className="icomoon text-2xl before:content-['\e960']" />
                  )}
                  {index === 3 && (
                    <i className="icomoon text-2xl bf-icon-pay-emi" />
                  )}
                  {index === 4 && (
                    <i className="icomoon text-2xl before:content-['\e958']" />
                  )}
                  {index === 5 && (
                    <i className="icomoon text-2xl before:content-['\e964b']" />
                  )}
                  <span className="py-1 text-[10px]">{item.label}</span>
                </BFLLink>
              </li>
            );
          })}
      </ul>

      <Conditional if={activePanel === "quickActions"}>
        <button
          className="z-7 fixed inset-0 border-none bg-[rgba(0,0,0,0.5)] bg-opacity-50"
          onClick={() => setActivePanel("none")}
          aria-label="Close quick actions"
        />
      </Conditional>

      <Conditional if={activePanel === "hamburger"}>
        <HamburgerMenu
          chipsDetails={chipsDetails}
          hamburgerMenuList={hamburgerMenuList}
          mobileFollowus={mobileFollowus}
          mainMenuList={mainMenuList}
          closeHamburgerMenu={() => {
            setActivePanel("none");
          }}
        />
      </Conditional>

      <Conditional if={activePanel === "quickActions"}>
        <QuickActions quickActions={quickActions} />
      </Conditional>
    </>
  );
};

export default BottomNavigation;
